import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/api';
import styles from './MatchingBatch.module.css';
import {
  Upload,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Coins,
  RotateCcw,
  ArrowRight,
  Trophy,
  Mail
} from 'lucide-react';
import MatchingResult from '../pages/MatchingResult';

const POLLING_TIMEOUT_MS  = 10 * 60 * 1000;
const POLLING_INTERVAL_MS = 5_000;

const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx';
const ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/* ── score helpers ── */
const getScoreVariant = (s) => s >= 75 ? 'high' : s >= 50 ? 'medium' : 'low';
const getScoreColor   = (s) =>
  s >= 75 ? 'var(--color-success,#10b981)'
  : s >= 50 ? 'var(--color-warning,#f59e0b)'
  : 'var(--color-error,#ef4444)';

/* ════════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
   ════════════════════════════════════════════════════════════ */
const MatchingBatch = ({ onBatchFinish, quota, refreshToken }) => {

  /* ── fichiers ── */
  const [jobFile,      setJobFile]      = useState(null);
  const [cvFiles,      setCvFiles]      = useState([]);
  const [isDraggingJob, setIsDraggingJob] = useState(false);
  const [isDraggingCv,  setIsDraggingCv]  = useState(false);
  const jobInputRef = useRef(null);
  const cvInputRef  = useRef(null);

  /* ── processus ── */
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tasks,        setTasks]        = useState([]);
  const [expandedId,   setExpandedId]   = useState(null);

  /* ── refs techniques ── */
  const tasksRef        = useRef([]);
  const pollingTimerRef = useRef(null);

  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  /* ════════════════════════════════════════════════════════
     1. GESTION FICHIERS
     ════════════════════════════════════════════════════════ */
  const validateFile = (file) => ALLOWED_MIME.includes(file?.type);

  const handleJobChange = (file) => {
    if (file && validateFile(file)) setJobFile(file);
  };

  const handleCvsChange = (files) => {
    const valid = Array.from(files).filter(validateFile);
    setCvFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !existing.has(f.name))];
    });
  };

  /* ── Handlers drop distincts (correction bug multi-fichiers) ── */
  const handleDropJob = (e) => {
    e.preventDefault();
    setIsDraggingJob(false);
    const file = e.dataTransfer.files[0];
    if (file) handleJobChange(file);
  };

  const handleDropCvs = (e) => {
    e.preventDefault();
    setIsDraggingCv(false);
    if (e.dataTransfer.files?.length) handleCvsChange(e.dataTransfer.files);
  };

  const removeCv  = (i) => setCvFiles((p) => p.filter((_, idx) => idx !== i));
  const removeJob = (e) => {
    e.stopPropagation();
    setJobFile(null);
    if (jobInputRef.current) jobInputRef.current.value = null;
  };

  /* ════════════════════════════════════════════════════════
     2. LANCEMENT
     ════════════════════════════════════════════════════════ */
  const startBatch = async () => {
    if (isSubmitting || !jobFile || cvFiles.length === 0) return;
    setIsSubmitting(true);
    setTasks([]);

    const fd = new FormData();
    fd.append('file_job', jobFile);
    cvFiles.forEach((f) => fd.append('file_cvs', f));

    try {
      const { data } = await api.post('/matching/batch-process', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.new_token) refreshToken(data.new_token);

      const initial = data.tasks.map((t) => ({
        id:           t.id,
        cv_name:      t.cv_name,
        status:       'processing',
        result:       null,
        score:        0,
        errorMessage: null,
      }));

      setTasks(initial);
      saveLS(jobFile.name, initial, Date.now());
      setIsProcessing(true);
    } catch (err) {
      alert('Erreur lors du lancement : ' + (err.response?.data?.error || err.message));
      setTasks([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════════════
     3. POLLING
     ════════════════════════════════════════════════════════ */
  const saveLS = (jobName, t, startTime) =>
    localStorage.setItem('batchTasks', JSON.stringify({ jobName, tasks: t, startTime }));

  const stopPolling = useCallback(() => {
    setIsProcessing(false);
    localStorage.removeItem('batchTasks');
    if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    onBatchFinish?.();
  }, [onBatchFinish]);

  const performCheck = useCallback(async () => {
    const current    = tasksRef.current;
    const processing = current.filter((t) => t.status === 'processing');

    if (processing.length === 0 && current.length > 0) { stopPolling(); return; }
    if (processing.length === 0) return;

    const saved     = JSON.parse(localStorage.getItem('batchTasks') || '{}');
    const startTime = saved.startTime ?? Date.now();
    const isTimeout = Date.now() - startTime > POLLING_TIMEOUT_MS;

    try {
      const { data } = await api.post('/status/batch', {
        task_ids: processing.map((t) => t.id),
      });
      const results = data.results ?? [];

      setTasks((prev) => {
        let changed = false;

        const next = prev.map((task) => {
          if (task.status !== 'processing') return task;

          const upd = results.find((r) => r.id === task.id);

          if (upd?.status === 'completed') {
            changed = true;
            const score = parseInt(
              upd.result?.global_score || upd.result?.matching_percentage || 0
            );
            return { ...task, status: 'completed', result: upd.result, score };
          }

          if (upd?.status === 'error') {
            changed = true;
            // On récupère le vrai message amical envoyé par app.py
            const errMsg = upd.result?.message || 'Erreur serveur';
            return { ...task, status: 'error', errorMessage: errMsg };
          }

          if (isTimeout) {
            changed = true;
            return { ...task, status: 'error', errorMessage: 'Délai dépassé (10 min)' };
          }

          return task;
        });

        if (changed) {
          const sorted = [...next].sort((a, b) => b.score - a.score);
          saveLS(saved.jobName ?? '', sorted, startTime);
          return sorted;
        }

        return prev;
      });
    } catch {
      /* réseau — on réessaiera au prochain tick */
    }
  }, [stopPolling]);

  /* ── Effets ── */

  // Restauration session au montage
  useEffect(() => {
    const raw = localStorage.getItem('batchTasks');
    if (!raw) return;
    try {
      const { tasks: t } = JSON.parse(raw);
      setTasks(t);
      if (t.some((x) => x.status === 'processing')) setIsProcessing(true);
    } catch {
      localStorage.removeItem('batchTasks');
    }
  }, []);

  // Gestion du timer (start / stop)
  useEffect(() => {
    if (!isProcessing) {
      if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
      return;
    }
    // Check immédiat puis intervalle régulier
    performCheck();
    pollingTimerRef.current = setInterval(performCheck, POLLING_INTERVAL_MS);
    return () => {
      if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    };
  }, [isProcessing, performCheck]);

  // Vérification sur retour d'onglet
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isProcessing) performCheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isProcessing, performCheck]);

  /* ── Stats ── */
  const completed  = tasks.filter((t) => t.status === 'completed');
  const processing = tasks.filter((t) => t.status === 'processing');
  const errored    = tasks.filter((t) => t.status === 'error');
  const topScore   = completed.length > 0
    ? Math.max(...completed.map((t) => t.score))
    : null;

  /* ════════════════════════════════════════════════════════
     4. RENDU
     ════════════════════════════════════════════════════════ */
  const showForm    = !isProcessing && tasks.length === 0;
  const showResults =  isProcessing || tasks.length > 0;

  return (
    <div className={styles.batchWrapper}>

      {/* ══════════════════════════════════════════
          VUE 1 — FORMULAIRE
          ══════════════════════════════════════════ */}
      {showForm && (
        <div className={styles.formSection}>

          {/* Header */}
          <div className={styles.formHeader}>
            <div className={styles.formTitleRow}>
              <div className={styles.formIconWrap}>
                <Layers size={18} strokeWidth={2} />
              </div>
              <div>
                <h2 className={styles.formTitle}>Comparaison multiple</h2>
                <p className={styles.formDesc}>
                  Classez automatiquement jusqu'à 20 CVs face à une fiche de poste.
                </p>
              </div>
            </div>

            {quota != null && (
              <div className={styles.quotaChip}>
                <Coins size={13} strokeWidth={2} />
                <span><strong>{quota}</strong> crédit{quota > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {/* Drop grid */}
          <div className={styles.dropGrid}>

            {/* ── Zone Fiche de Poste ── */}
            <div
              className={`${styles.dropZone}
                ${jobFile       ? styles.dzActive   : ''}
                ${isDraggingJob ? styles.dzDragging : ''}`}
              onClick={() => !jobFile && jobInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingJob(true); }}
              onDragLeave={() => setIsDraggingJob(false)}
              onDrop={handleDropJob}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && !jobFile && jobInputRef.current?.click()}
              aria-label="Zone fiche de poste"
            >
              <input
                type="file"
                hidden
                ref={jobInputRef}
                accept={ALLOWED_EXTENSIONS}
                onChange={(e) => handleJobChange(e.target.files[0])}
              />

              {jobFile ? (
                <>
                  <button className={styles.removeBtn} onClick={removeJob} type="button">
                    <X size={13} strokeWidth={2.5} />
                  </button>
                  <div className={styles.fileInfo}>
                    <div className={styles.fileIconWrap}>
                      <FileText size={26} strokeWidth={1.5} />
                    </div>
                    <p className={styles.fileTypeLabel}>Fiche de poste</p>
                    <p className={styles.fileName}>{jobFile.name}</p>
                  </div>
                </>
              ) : (
                <div className={styles.emptyZone}>
                  <div className={styles.uploadIconWrap}>
                    <FileText size={24} strokeWidth={1.5} />
                  </div>
                  <p className={styles.dzLabel}>Fiche de Poste</p>
                  <p className={styles.dzHint}>
                    Glissez ou <span className={styles.browse}>parcourez</span>
                  </p>
                  <p className={styles.dzFormats}>PDF · DOC · DOCX</p>
                </div>
              )}
            </div>

            {/* Séparateur */}
            <div className={styles.gridDivider} aria-hidden="true">
              <div className={styles.divLine} />
              <ArrowRight size={16} strokeWidth={1.5} className={styles.divIcon} />
              <div className={styles.divLine} />
            </div>

            {/* ── Zone CVs multiples ── */}
            <div
              className={`${styles.dropZone} ${styles.dropZoneCvs}
                ${isDraggingCv  ? styles.dzDragging : ''}
                ${cvFiles.length > 0 ? styles.dzActive : ''}`}
              onClick={() => cvInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingCv(true); }}
              onDragLeave={() => setIsDraggingCv(false)}
              onDrop={handleDropCvs}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && cvInputRef.current?.click()}
              aria-label="Zone CVs multiples"
            >
              <input
                type="file"
                hidden
                multiple
                ref={cvInputRef}
                accept={ALLOWED_EXTENSIONS}
                onChange={(e) => handleCvsChange(e.target.files)}
              />

              {/* Prompt toujours visible */}
              <div className={styles.emptyZone}>
                <div className={styles.uploadIconWrap}>
                  <Upload size={24} strokeWidth={1.5} />
                </div>
                <p className={styles.dzLabel}>CVs des candidats</p>
                <p className={styles.dzHint}>
                  {cvFiles.length > 0
                    ? <span className={styles.browse}>Ajouter d'autres fichiers</span>
                    : <>Glissez ou <span className={styles.browse}>parcourez</span></>}
                </p>
                <p className={styles.dzFormats}>PDF · DOC · DOCX — max 20 fichiers</p>
              </div>

              {/* Tags CVs */}
              {cvFiles.length > 0 && (
                <div
                  className={styles.cvTagList}
                  onClick={(e) => e.stopPropagation()}
                >
                  {cvFiles.map((f, i) => (
                    <div key={i} className={styles.cvTag}>
                      <FileText size={11} strokeWidth={2} />
                      <span className={styles.cvTagName}>{f.name}</span>
                      <button
                        className={styles.cvTagRemove}
                        onClick={(e) => { e.stopPropagation(); removeCv(i); }}
                        type="button"
                      >
                        <X size={11} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className={styles.submitRow}>
            <button
              className={styles.submitBtn}
              onClick={startBatch}
              disabled={!jobFile || cvFiles.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} strokeWidth={2} className={styles.spin} />
                  Lancement en cours…
                </>
              ) : (
                <>
                  <Layers size={16} strokeWidth={2} />
                  Lancer l'analyse — {cvFiles.length} crédit{cvFiles.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          VUE 2 — RÉSULTATS
          ══════════════════════════════════════════ */}
      {showResults && (
        <div className={styles.resultsSection}>

          {/* Header résultats */}
          <div className={styles.resultsHeader}>
            <div className={styles.resultsHeaderLeft}>
              <h3 className={styles.resultsTitle}>Classement des candidats</h3>
              {isProcessing && (
                <span className={styles.processingBadge}>
                  <Loader2 size={13} strokeWidth={2} className={styles.spin} />
                  {processing.length} analyse{processing.length > 1 ? 's' : ''} en cours…
                </span>
              )}
            </div>

            {/* Stats de progression */}
            <div className={styles.progressStats}>
              {topScore !== null && (
                <div className={styles.statPill} data-variant="success">
                  <Trophy size={12} strokeWidth={2} />
                  Meilleur : {topScore}%
                </div>
              )}
              <div className={styles.statPill}>
                <CheckCircle2 size={12} strokeWidth={2} />
                {completed.length}/{tasks.length} terminés
              </div>
              {errored.length > 0 && (
                <div className={styles.statPill} data-variant="error">
                  <AlertTriangle size={12} strokeWidth={2} />
                  {errored.length} erreur{errored.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Barre de progression globale */}
          {isProcessing && tasks.length > 0 && (
            <div className={styles.globalProgress}>
              <div
                className={styles.globalProgressFill}
                style={{ width: `${(completed.length / tasks.length) * 100}%` }}
              />
            </div>
          )}

          {/* Liste des résultats */}
          <div className={styles.resultsList}>
            {tasks.map((task, idx) => {
              const isExpanded = expandedId === task.id;
              const isTop      = idx === 0 && task.status === 'completed';

              return (
                <div key={task.id} className={styles.resultItem}>
                  <div
                    className={`${styles.resultRow}
                      ${task.status === 'completed' ? styles.rowCompleted : ''}
                      ${isTop                       ? styles.rowTop       : ''}
                      ${task.status === 'error'     ? styles.rowError     : ''}`}
                    onClick={() =>
                      task.status === 'completed' &&
                      setExpandedId(isExpanded ? null : task.id)
                    }
                    role={task.status === 'completed' ? 'button' : undefined}
                    tabIndex={task.status === 'completed' ? 0 : undefined}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      task.status === 'completed' &&
                      setExpandedId(isExpanded ? null : task.id)
                    }
                  >
                    {/* Rang */}
                    <div className={`${styles.rank} ${isTop ? styles.rankTop : ''}`}>
                      {isTop
                        ? <Trophy size={16} strokeWidth={2} className={styles.trophyIcon} />
                        : <span>#{idx + 1}</span>}
                    </div>

                    {/* Nom CV */}
                    <div className={styles.cvName}>
                      <FileText size={14} strokeWidth={1.75} className={styles.cvNameIcon} />
                      <span>{task.cv_name}</span>
                    </div>

                    {/* Score / statut */}
                    <div className={styles.scoreArea}>
                      {task.status === 'completed' && (
                        <div
                          className={styles.scorePill}
                          style={{ '--score-color': getScoreColor(task.score) }}
                        >
                          <span className={styles.scoreNum}>{task.score}%</span>
                          <div className={styles.scoreMini}>
                            <div
                              className={styles.scoreMiniBar}
                              style={{
                                width:      `${task.score}%`,
                                background: getScoreColor(task.score),
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {task.status === 'processing' && (
                        <span className={styles.statusProcessing}>
                          <Loader2 size={13} strokeWidth={2} className={styles.spin} />
                          Analyse…
                        </span>
                      )}
                      {task.status === 'error' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span className={styles.statusError} title={task.errorMessage}>
                            <AlertTriangle size={13} strokeWidth={2} />
                            Erreur IA
                          </span>
                          <a href="/support" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: 'var(--color-error)', textDecoration: 'underline' }}>
                            <Mail size={12} strokeWidth={2} /> Signaler
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Expand icon */}
                    {task.status === 'completed' && (
                      <div className={styles.expandIcon}>
                        {isExpanded
                          ? <ChevronUp   size={16} strokeWidth={2} />
                          : <ChevronDown size={16} strokeWidth={2} />}
                      </div>
                    )}
                  </div>

                  {/* Détail accordéon */}
                  {isExpanded && task.result && (
                    <div className={styles.resultDetail}>
                      <MatchingResult data={task.result} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bouton reset — seulement quand tout est fini */}
          {!isProcessing && (
            <div className={styles.resetRow}>
              <button
                className={styles.resetBtn}
                onClick={() => {
                  setTasks([]);
                  setCvFiles([]);
                  setJobFile(null);
                  localStorage.removeItem('batchTasks');
                }}
              >
                <RotateCcw size={15} strokeWidth={2} />
                Nouvelle analyse
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchingBatch;